import { Button, Combobox, Dialog, RadioGroup, Label } from "@/components/ui";
import React, { useEffect, useState } from "react";
import { PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  onComplete: (playerId?: number | string) => void;
}

export const VideoPageOwnerModal: React.FC<Props> = ({ open, onOpenChange, title = "New Video Page", onComplete }) => {
  const [ownerType, setOwnerType] = useState<"MY_LIBRARY" | "PLAYER">("MY_LIBRARY");

  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  };

  const players = usePaginatedCombobox(getPlayers, false);

  useEffect(() => {
    if (open) {
      setOwnerType("MY_LIBRARY");
    }
  }, [open]);

  const handleCreate = () => {
    if (ownerType === "MY_LIBRARY") {
      onComplete(undefined);
      onOpenChange(false);
    } else if (players.value) {
      onComplete(players.value.id);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <Dialog.Header>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description>Where should this video page be saved?</Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-6 py-4">
        <RadioGroup value={ownerType} onValueChange={val => setOwnerType(val as any)}>
          <RadioGroup.Button value="MY_LIBRARY" label="My Library" />
          <RadioGroup.Button value="PLAYER" label="A Player's Library" />
        </RadioGroup>

        {ownerType === "PLAYER" && (
          <div className="flex flex-col gap-2">
            <Label>Select Player</Label>
            <Combobox {...players} placeholder="Search for a Player..." className="w-full" />
          </div>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <div className="flex justify-end gap-2 w-full">
          <Dialog.Close render={<Button type="button" variant="outline" children="Cancel" />} />
          <Button type="button" disabled={ownerType === "PLAYER" && !players.value} onClick={handleCreate}>
            Create
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
};
